/**
 * Login Button Component
 * 
 * GitHub/이메일 로그인 버튼
 */

import { useEffect, useState } from 'react';
import { useAuth } from '../auth';
import { useLanguage } from '../i18n';
import { analytics } from '../analytics';
import EmailAuthModal from './EmailAuthModal';

export default function LoginButton() {
  const { t } = useLanguage();
  const { startGithubLogin, isLoading, startLoggingIn, cancelLoggingIn } = useAuth();
  
  const [showEmailAuthModal, setShowEmailAuthModal] = useState(false);
  
  const [showAuthModal, setShowAuthModal] = useState(false);

  const disabled = isLoading;

  // ESC로 로그인 모달 닫기
  useEffect(() => {
    if (!showAuthModal) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowAuthModal(false);
        cancelLoggingIn();  // 딤 오버레이 종료
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showAuthModal, cancelLoggingIn]);

  return (
    <>
      <button
        onClick={() => {
          setShowAuthModal(true);
          startLoggingIn();  // 딤 오버레이 시작
        }}
        disabled={disabled}
        data-testid="login-button"
        className="flex items-center gap-2 px-3 py-1.5 border border-terminal-border rounded text-sm text-terminal-text hover:bg-terminal-dim/30 hover:border-status-go/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span>{disabled ? '...' : t.auth.login}</span>
      </button>

      {showAuthModal && (
        <div
          className="fixed inset-0 z-[10002] flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowAuthModal(false);
              cancelLoggingIn();  // 딤 오버레이 종료
            }
          }}
        >
          <div className="bg-terminal-bg border border-terminal-border rounded-xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-terminal-border bg-terminal-dim/20">
              <h3 className="text-base font-semibold text-terminal-text">{t.auth.quickLoginTitle}</h3>
              <p className="text-xs text-terminal-muted mt-1">{t.auth.quickLoginDesc}</p>
            </div>

            <div className="p-6 space-y-3">
              <button
                onClick={() => {
                  setShowAuthModal(false);
                  cancelLoggingIn();
                  analytics.loginClick();
                  setShowEmailAuthModal(true);
                }}
                disabled={disabled}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-terminal-border rounded-lg text-sm text-terminal-text hover:bg-terminal-dim/30 hover:border-status-go/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="text-base leading-none">✉️</span>
                <span>{t.auth.loginWithEmail}</span>
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-terminal-border"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-2 bg-terminal-bg text-terminal-muted">{t.auth.or}</span>
                </div>
              </div>

              <button
                onClick={() => {
                  setShowAuthModal(false);
                  analytics.loginClick();
                  startGithubLogin();
                }}
                disabled={disabled}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-terminal-border rounded-lg text-sm text-terminal-text hover:bg-terminal-dim/30 hover:border-status-go/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="text-base leading-none">🐙</span>
                <span>{t.auth.loginWithGithub}</span>
              </button>
            </div>

            <div className="px-6 py-4 border-t border-terminal-border bg-terminal-dim/10">
              <button
                onClick={() => {
                  setShowAuthModal(false);
                  cancelLoggingIn();  // 딤 오버레이 종료
                }}
                disabled={disabled}
                className="w-full px-4 py-2 text-xs text-terminal-muted hover:text-terminal-text transition-colors"
              >
                {t.terms.cancel}
              </button>
            </div>
          </div>
        </div>
      )}

      <EmailAuthModal
        isOpen={showEmailAuthModal}
        onClose={() => setShowEmailAuthModal(false)}
        initialMode="login"
      />
    </>
  );
}
